import { Alert, AlertTitle, Button, Typography, styled } from '@mui/material';
import {
  WarningAmber,
  Warning,
  Clear,
  FileDownload,
  Info,
} from '@mui/icons-material';
import { Box } from '@mui/system';
import DOMPurify from 'dompurify';
import { download } from 'src/utils/download';
import { prepareForHtmlDisplay } from 'src/utils/html';
import { useTranslation } from 'react-i18next';

const StyledAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(),
  marginBottom: theme.spacing(),
}));

export function FormStatus({
  item: { is_verification_required, is_expired, expires_soon },
  formProgress: { is_accepted } = {},
}) {
  const { t } = useTranslation();
  if (is_verification_required && is_accepted === null) {
    return (
      <StyledAlert color="warning" icon={<WarningAmber />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.answers-pending')}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === 0) {
    return (
      <StyledAlert color="error" icon={<Clear />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.answers-rejected')}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === 1) {
    if (is_expired) {
      return (
        <StyledAlert color="error">
          <AlertTitle>
            {t('corporate-onboarding.status-messages.item-expired-answers')}
          </AlertTitle>
        </StyledAlert>
      );
    } else if (expires_soon) {
      return (
        <StyledAlert color="warning">
          <AlertTitle>
            {t('corporate-onboarding.status-messages.item-expiring-answers')}
          </AlertTitle>
        </StyledAlert>
      );
    } else {
      return (
        <StyledAlert color="success">
          <AlertTitle>
            {t('corporate-onboarding.status-messages.answers-approved')}
          </AlertTitle>
        </StyledAlert>
      );
    }
  } else {
    return null;
  }
}

export function AgreementStatus({ item }) {
  const { t, i18n } = useTranslation();
  if (item.completed) {
    if (item.is_expired) {
      return (
        <StyledAlert color="error" icon={<Warning />}>
          <AlertTitle>
            {t('corporate-onboarding.status-messages.agreement-expired')}
          </AlertTitle>
        </StyledAlert>
      );
    } else if (item.expires_soon) {
      return (
        <StyledAlert color="warning" icon={<Warning />}>
          <AlertTitle>
            {t('corporate-onboarding.status-messages.agreement-expiring')}
          </AlertTitle>
        </StyledAlert>
      );
    } else if (!item.agreed) {
      return (
        <StyledAlert color="warning" icon={<Warning />}>
          <AlertTitle>
            {t(
              'corporate-onboarding.status-messages.agreement-force-completed',
              {
                name: item.completed_by,
              }
            )}
          </AlertTitle>
        </StyledAlert>
      );
    } else {
      // Agreement is completed and agreed to successfully
      return (
        <StyledAlert color="success">
          <AlertTitle>
            {t('corporate-onboarding.status-messages.agreement-completed', {
              name: item.completed_by,
              date: new Date(item.completed).toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
            })}
          </AlertTitle>
        </StyledAlert>
      );
    }
  } else if (item.agreed) {
    return (
      <StyledAlert color="success">
        <AlertTitle>
          {t('corporate-onboarding.status-messages.agreed-on-behalf', {
            name: item.completed_by,
            org: item.npo_name,
          })}
        </AlertTitle>
      </StyledAlert>
    );
  } else {
    return null;
  }
}

export function DocumentStatus({
  item: {
    completed,
    completed_by,
    is_verification_required,
    is_expired,
    expires_soon,
  } = {},
  documentProgress: { is_accepted, signed_document } = {},
}) {
  const { t } = useTranslation();
  if (is_verification_required && is_accepted === 1) {
    if (is_expired) {
      return (
        <StyledAlert color="warning" icon={<Warning />}>
          <AlertTitle>
            {t('corporate-onboarding.status-messages.item-expired-upload')}
          </AlertTitle>
        </StyledAlert>
      );
    } else if (expires_soon) {
      return (
        <StyledAlert color="warning" icon={<Warning />}>
          <AlertTitle>
            {t('corporate-onboarding.status-messages.item-expiring-upload')}
          </AlertTitle>
        </StyledAlert>
      );
    }
  }

  if (!signed_document && completed) {
    return (
      <StyledAlert color="warning" icon={<Warning />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.document-force-completed', {
            name: completed_by,
          })}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === null) {
    return (
      <StyledAlert color="warning" icon={<WarningAmber />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.document-pending')}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === 1) {
    return (
      <StyledAlert color="success">
        <AlertTitle>
          {t('corporate-onboarding.status-messages.document-approved')}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === 0) {
    return (
      <StyledAlert color="error" icon={<Clear />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.document-rejected')}
        </AlertTitle>
      </StyledAlert>
    );
  } else {
    return null;
  }
}

export function UploadStatus({
  item: {
    completed,
    completed_by,
    is_verification_required,
    is_expired,
    expires_soon,
  } = {},
  uploadProgress: { is_accepted, upload } = {},
}) {
  const { t } = useTranslation();
  if (is_verification_required && is_accepted === 1) {
    if (is_expired) {
      return (
        <StyledAlert color="warning" icon={<Warning />}>
          <AlertTitle>
            {t('corporate-onboarding.status-messages.item-expired-upload')}
          </AlertTitle>
        </StyledAlert>
      );
    } else if (expires_soon) {
      return (
        <StyledAlert color="warning" icon={<Warning />}>
          <AlertTitle>
            {t('corporate-onboarding.status-messages.item-expiring-upload')}
          </AlertTitle>
        </StyledAlert>
      );
    }
  }

  if (!upload && completed) {
    return (
      <StyledAlert color="warning" icon={<Warning />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.task-force-completed', {
            name: completed_by,
          })}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === null) {
    return (
      <StyledAlert color="warning" icon={<WarningAmber />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.file-pending')}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === 1) {
    return (
      <StyledAlert color="success">
        <AlertTitle>
          {t('corporate-onboarding.status-messages.file-approved')}
        </AlertTitle>
      </StyledAlert>
    );
  } else if (is_verification_required && is_accepted === 0) {
    return (
      <StyledAlert color="error" icon={<Clear />}>
        <AlertTitle>
          {t('corporate-onboarding.status-messages.file-rejected')}
        </AlertTitle>
      </StyledAlert>
    );
  } else {
    return null;
  }
}

export function FileWidget({
  name,
  title,
  url,
  callToAction = false,
  hideDownload = false,
}) {
  const { t } = useTranslation();
  return (
    <Box width="100%" display="flex" minHeight="500px" flexDirection="column">
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        my={2}
      >
        {title && <Typography variant="h6">{title}</Typography>}

        {hideDownload ? null : (
          <Button
            variant={callToAction ? 'outlined' : 'text'}
            sx={{ ml: 1 }}
            onClick={() => download(url, name)}
            startIcon={<FileDownload />}
          >
            {t('common.download')}
          </Button>
        )}
      </Box>

      <iframe
        style={{ flex: 1 }}
        src={url}
        title={t('corporate-onboarding.document-viewer', { name })}
      />
    </Box>
  );
}

export function Instructions({ instructions }) {
  const { t } = useTranslation();
  const htmlContent = prepareForHtmlDisplay(instructions);

  return (
    <StyledAlert color="info" icon={<Info />} sx={{ mt: 2 }}>
      <AlertTitle>{t('donations.instructions')}</AlertTitle>

      <div
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }}
      />
    </StyledAlert>
  );
}
